import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { DifferentialDrive } from '../drive/differential';
import type { RobotProfile } from '../profiles/types';
import type { SafetyOutput } from './safety';
import { ROBOT_LAYER, type SimWorld } from './world';
import { DEFAULT_SURFACE, type SurfaceProps } from './surfaces';

const DEG = Math.PI / 180;
const UP = new THREE.Vector3(0, 1, 0);

function clampAbs(v: number, max: number): number {
  return Math.max(-max, Math.min(max, v));
}

export interface RobotState {
  pos: THREE.Vector3;
  headingDeg: number;
  speed: number;
  yawRateDps: number;
  grounded: boolean;
  slopeDeg: number;
  surface: SurfaceProps;
  batteryPct: number;
}

/** Робот: физическое тело + привод + камеры. Камеры — единственный источник
 *  информации оператора о внешнем мире, поэтому живут вместе с телом. */
export class Robot {
  readonly body: RAPIER.RigidBody;
  readonly collider: RAPIER.Collider;
  readonly drive: DifferentialDrive;
  readonly group = new THREE.Group();
  readonly cameras = new Map<string, THREE.PerspectiveCamera>();
  private cameraBase = new Map<string, THREE.Vector3>();

  private battery = 100;
  private shakePhase = 0;
  private lastStepAt = -Infinity;
  private stepEvents: Array<{ t: number; height: number }> = [];
  private smoothNormal = new THREE.Vector3(0, 1, 0);
  private start: { pos: [number, number, number]; heading: number };

  state: RobotState = {
    pos: new THREE.Vector3(),
    headingDeg: 0,
    speed: 0,
    yawRateDps: 0,
    grounded: true,
    slopeDeg: 0,
    surface: DEFAULT_SURFACE,
    batteryPct: 100,
  };

  constructor(
    private world: SimWorld,
    public readonly profile: RobotProfile,
    startPos: [number, number, number] = [0, 0.4, 0],
    startHeadingDeg = 0,
  ) {
    this.start = { pos: startPos, heading: startHeadingDeg };
    this.drive = new DifferentialDrive(profile);

    const d = profile.dimensions_m;
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, startHeadingDeg * DEG, 0));
    this.body = world.physics.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(startPos[0], startPos[1], startPos[2])
        .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w })
        .setAngularDamping(2.5)
        .setLinearDamping(0.05)
        .setCcdEnabled(true),
    );

    // Скруглённые рёбра: радиус скругления = предельная преодолимая ступенька
    // из профиля, поэтому бордюр «по паспорту» робот переезжает, а высокий — нет.
    const r = Math.max(0.01, profile.max_obstacle_height_m);
    this.collider = world.physics.createCollider(
      RAPIER.ColliderDesc.roundCuboid(d.w / 2 - r, d.h / 2 - r, d.l / 2 - r, r)
        .setMass(profile.mass_kg)
        .setFriction(0.7)
        .setRestitution(0.05)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      this.body,
    );
    world.register(this.collider.handle, 'робот');

    this.buildVisual();
    this.buildCameras();
    // Весь корпус — на отдельный слой, иначе он попадает в кадр своих же камер
    this.group.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) o.layers.set(ROBOT_LAYER);
    });
    world.scene.add(this.group);
  }

  private buildVisual(): void {
    const d = this.profile.dimensions_m;
    const shell = new THREE.Mesh(
      new THREE.BoxGeometry(d.w, d.h * 0.75, d.l),
      new THREE.MeshLambertMaterial({ color: 0xe8eaed }),
    );
    shell.position.y = -d.h * 0.08;
    shell.castShadow = true;
    this.group.add(shell);

    const lid = new THREE.Mesh(
      new THREE.BoxGeometry(d.w * 0.9, d.h * 0.2, d.l * 0.85),
      new THREE.MeshLambertMaterial({ color: 0x2f7de1 }),
    );
    lid.position.y = d.h * 0.36;
    lid.castShadow = true;
    this.group.add(lid);

    // Маркер «перёд» — чтобы в инструкторском виде было видно ориентацию
    const nose = new THREE.Mesh(
      new THREE.BoxGeometry(d.w * 0.35, 0.05, 0.06),
      new THREE.MeshLambertMaterial({ color: 0xffcc33 }),
    );
    nose.position.set(0, d.h * 0.3, d.l / 2);
    this.group.add(nose);

    const wheelGeo = new THREE.CylinderGeometry(0.11, 0.11, 0.07, 12);
    const wheelMat = new THREE.MeshLambertMaterial({ color: 0x1b1d20 });
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const w = new THREE.Mesh(wheelGeo, wheelMat);
        w.rotation.z = Math.PI / 2;
        w.position.set((sx * this.profile.track_m) / 2, -d.h / 2 + 0.08, (sz * this.profile.wheelbase_m) / 2);
        this.group.add(w);
      }
    }
  }

  private buildCameras(): void {
    for (const c of this.profile.cameras) {
      const cam = new THREE.PerspectiveCamera(c.fov_deg, c.res[0] / c.res[1], 0.05, 400);
      const base = new THREE.Vector3(c.pos[0], c.pos[1] - this.profile.dimensions_m.h / 2, c.pos[2]);
      cam.position.copy(base);
      cam.rotation.order = 'YXZ';
      cam.rotation.y = (c.yaw_deg ?? 0) * DEG + Math.PI;
      cam.rotation.x = c.pitch_deg * DEG;
      this.group.add(cam);
      this.cameras.set(c.id, cam);
      this.cameraBase.set(c.id, base);
    }
  }

  /** Луч вниз: покрытие, уклон, контакт с землёй */
  private probeGround(): { grounded: boolean; normal: THREE.Vector3; slopeDeg: number; groundY: number } {
    const p = this.body.translation();
    const ray = new RAPIER.Ray({ x: p.x, y: p.y, z: p.z }, { x: 0, y: -1, z: 0 });
    const maxToi = this.profile.dimensions_m.h / 2 + 0.35;
    const hit = this.world.physics.castRayAndGetNormal(
      ray,
      maxToi,
      true,
      undefined,
      undefined,
      this.collider,
    );
    if (!hit) {
      return { grounded: false, normal: new THREE.Vector3(0, 1, 0), slopeDeg: 0, groundY: p.y - maxToi };
    }
    const n = new THREE.Vector3(hit.normal.x, hit.normal.y, hit.normal.z).normalize();
    const slopeDeg = Math.acos(Math.min(1, Math.abs(n.y))) / DEG;
    const grounded = hit.timeOfImpact <= this.profile.dimensions_m.h / 2 + 0.12;
    return { grounded, normal: n, slopeDeg, groundY: p.y - hit.timeOfImpact };
  }

  /**
   * Заезд на ступеньку. Преодолимость определяется профилем робота
   * (`max_obstacle_height_m`), а не физикой коллайдера: щуп у самой земли ищет
   * препятствие по курсу, второй щуп меряет его высоту. Паспортный бордюр
   * робот переезжает, бордюр выше паспортного — упирается и стоит.
   */
  private stepAssist(now: number, fFlat: THREE.Vector3, dirSign: number, groundY: number): void {
    if (now - this.lastStepAt < 260) return;
    const p = this.body.translation();
    const half = this.profile.dimensions_m.l / 2;
    const maxStep = this.profile.max_obstacle_height_m;

    const dir = { x: fFlat.x * dirSign, y: 0, z: fFlat.z * dirSign };
    const low = new RAPIER.Ray(
      { x: p.x + dir.x * half * 0.85, y: groundY + 0.015, z: p.z + dir.z * half * 0.85 },
      dir,
    );
    const blocked = this.world.physics.castRay(low, 0.2, true, undefined, undefined, this.collider);
    if (!blocked) return;

    // Что там по высоте: щуп сверху вниз чуть дальше препятствия
    const px = p.x + dir.x * (half + 0.14);
    const pz = p.z + dir.z * (half + 0.14);
    const fromY = groundY + maxStep + 0.2;
    const down = new RAPIER.Ray({ x: px, y: fromY, z: pz }, { x: 0, y: -1, z: 0 });
    const top = this.world.physics.castRay(
      down,
      maxStep + 0.45,
      true,
      undefined,
      undefined,
      this.collider,
    );
    const stepH = top ? fromY - top.timeOfImpact - groundY : 0;
    if (stepH < 0.015 || stepH > maxStep + 0.005) return;

    // Вертикальный импульс ровно на высоту ступеньки: робот «переступает» бордюр
    const v = Math.sqrt(2 * 9.81 * (stepH + 0.04));
    const lin = this.body.linvel();
    this.body.setLinvel({ x: lin.x, y: Math.max(lin.y, v), z: lin.z }, true);
    this.lastStepAt = now;
    this.stepEvents.push({ t: now, height: stepH });
  }

  /**
   * Стабилизация корпуса: колёсное шасси на подвеске держится параллельно
   * опоре, а не кувыркается через нос на съезде с бордюра. Крен и тангаж
   * подтягиваются к нормали поверхности, курсовое вращение остаётся за
   * приводом. Опрокидывание ровера в MVP не моделируется.
   */
  private level(quat: THREE.Quaternion, targetUp: THREE.Vector3, grounded: boolean, yawRateDps: number): void {
    // Нормаль сглаживается: на переломе профиля (гребень пандуса, край бордюра)
    // она скачком меняется, и жёсткая стабилизация начала бы драться с рельефом
    this.smoothNormal.lerp(targetUp, 0.12).normalize();

    const up = UP.clone().applyQuaternion(quat);
    const axis = up.clone().cross(this.smoothNormal);
    const sin = Math.min(1, axis.length());
    if (sin > 1e-4) axis.divideScalar(sin);
    const angle = Math.asin(sin) * Math.sign(up.dot(this.smoothNormal) >= 0 ? 1 : -1);
    const k = grounded ? 7 : 2.5;
    const corr = axis.multiplyScalar(angle * k);

    // Робот смотрит вдоль +Z, значит его правый борт — это -X (правая тройка).
    // Поэтому команда «вправо» (yaw > 0) — это отрицательное вращение вокруг Y.
    this.body.setAngvel(
      { x: clampAbs(corr.x, 3), y: grounded ? -yawRateDps * DEG : this.body.angvel().y, z: clampAbs(corr.z, 3) },
      true,
    );
  }

  /** Забрать события заезда на ступеньки (для лога) */
  takeStepEvents(): Array<{ t: number; height: number }> {
    const e = this.stepEvents;
    this.stepEvents = [];
    return e;
  }

  update(now: number, dt: number, out: SafetyOutput): void {
    const t = this.body.translation();
    const rot = this.body.rotation();
    const quat = new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w);

    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(quat);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(quat);
    const fFlat = new THREE.Vector3(forward.x, 0, forward.z);
    if (fFlat.lengthSq() < 1e-6) fFlat.set(0, 0, 1);
    fFlat.normalize();
    const rFlat = new THREE.Vector3(right.x, 0, right.z).normalize();

    const ground = this.probeGround();
    const surf = ground.grounded ? this.world.surfaces.at(t.x, t.z) : DEFAULT_SURFACE;

    // Составляющая тяжести вдоль направления движения по склону
    let slopeAccel = 0;
    if (ground.grounded && ground.slopeDeg > 0.5) {
      const along = fFlat.clone().sub(ground.normal.clone().multiplyScalar(fFlat.dot(ground.normal)));
      if (along.lengthSq() > 1e-6) slopeAccel = -9.81 * along.normalize().y;
    }
    // Уклон круче паспортного — сцепление падает, робота начинает сползать
    const steep = ground.grounded && ground.slopeDeg > this.profile.max_safe_slope_deg;

    const lin = this.body.linvel();
    const actualLong = lin.x * fFlat.x + lin.z * fFlat.z;
    // Робота остановило препятствие — модель привода не должна ехать «сквозь стену».
    // Сверяемся только когда робот реально потерял ход: толчок от угла бордюра
    // на съезде не должен читаться как «уперлись» и разворачивать привод назад.
    if (Math.abs(actualLong) < Math.abs(this.drive.currentSpeed) - 0.35) {
      this.drive.syncSpeed(actualLong, this.drive.currentYawRate);
    }

    const effective: SurfaceProps = !ground.grounded
      ? { ...DEFAULT_SURFACE, grip: 0.05, rolling: 0 }
      : steep
        ? { ...surf, grip: surf.grip * 0.45, lateralDamp: surf.lateralDamp * 0.7 }
        : surf;
    const res = this.drive.step(now, dt, out, effective, ground.grounded ? slopeAccel : 0);

    // Боковой снос: на льду гасится медленно — робота «несёт»
    let lateral = lin.x * rFlat.x + lin.z * rFlat.z;
    lateral *= Math.pow(effective.lateralDamp, dt * 60);

    if (ground.grounded) {
      // Вверх робота подбрасывает только заезд на ступеньку. Всё остальное —
      // это солвер выталкивает корпус на углу бордюра; такой подскок гасим,
      // иначе робот прыгает на каждом съезде.
      const climbing = now - this.lastStepAt < 400;
      this.body.setLinvel(
        {
          x: fFlat.x * res.speed + rFlat.x * lateral,
          y: climbing ? lin.y : Math.min(lin.y, 0.12),
          z: fFlat.z * res.speed + rFlat.z * lateral,
        },
        true,
      );
      if (Math.abs(res.speed) > 0.08) {
        this.stepAssist(now, fFlat, Math.sign(res.speed), ground.groundY);
      }
    }

    this.level(quat, ground.grounded ? ground.normal : UP, ground.grounded, res.yawRate);

    this.battery = Math.max(0, this.battery - (0.0006 + Math.abs(res.speed) * 0.0009) * dt * 60);

    // Курс по-компасному: поворот вправо увеличивает значение
    const heading = -Math.atan2(fFlat.x, fFlat.z) / DEG;
    this.state = {
      pos: new THREE.Vector3(t.x, t.y, t.z),
      headingDeg: (heading + 360) % 360,
      speed: res.speed,
      yawRateDps: res.yawRate,
      grounded: ground.grounded,
      slopeDeg: ground.slopeDeg,
      surface: surf,
      batteryPct: this.battery,
    };

    this.syncVisual(quat, dt, surf, res.speed);
  }

  private syncVisual(quat: THREE.Quaternion, dt: number, surf: SurfaceProps, speed: number): void {
    const t = this.body.translation();
    this.group.position.set(t.x, t.y, t.z);
    this.group.quaternion.copy(quat);

    // Тряска камер от покрытия: основной канал «ощущения дороги» для оператора
    this.shakePhase += dt * (14 + Math.abs(speed) * 26);
    const amp = surf.vibration * Math.min(1, Math.abs(speed) / 0.8);
    for (const [id, cam] of this.cameras) {
      const base = this.cameraBase.get(id)!;
      cam.position.set(
        base.x + Math.sin(this.shakePhase * 1.7) * amp,
        base.y + Math.sin(this.shakePhase * 2.3) * amp * 1.6,
        base.z,
      );
      cam.rotation.z = Math.sin(this.shakePhase * 1.1) * amp * 0.8;
    }
  }

  /** Скорость в момент удара — для лога столкновений */
  get impactSpeed(): number {
    const v = this.body.linvel();
    return Math.hypot(v.x, v.z);
  }

  respawn(pos?: [number, number, number], headingDeg?: number): void {
    const p = pos ?? this.start.pos;
    const h = headingDeg ?? this.start.heading;
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, h * DEG, 0));
    this.body.setTranslation({ x: p[0], y: p[1], z: p[2] }, true);
    this.body.setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.drive.reset();
  }
}
